import { useState } from 'react';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useForumPublish } from '@/hooks/useForum';
import { genId } from '@/lib/forum';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

/** Inline reply box shown at the bottom of a thread (phpBB-style). */
export function ReplyForm({ threadId }: { threadId: string }) {
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const { user } = useCurrentUser();
  const { publishNode } = useForumPublish();
  const { toast } = useToast();

  if (!user) {
    return (
      <div className="mt-4 rounded-[6px] border border-[#d6d6d6] bg-[#f8f9fb] px-4 py-3 text-sm text-[#666]">
        You must be logged in to reply.
      </div>
    );
  }

  async function submit() {
    if (!content.trim()) {
      toast({ title: 'Please write a reply' });
      return;
    }
    setBusy(true);
    try {
      await publishNode({
        id: genId('msg'),
        parent: threadId,
        title: '',
        content: content.trim(),
        type: 'reply',
      });
      toast({ title: 'Reply posted' });
      setContent('');
    } catch (e) {
      toast({ title: 'Could not post reply', description: String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded-[6px] border border-[#d6d6d6] bg-white">
      <div className="border-b border-[#d6d6d6] bg-[linear-gradient(to_bottom,#f4f4f4,#e4e4e4)] px-3 py-2 text-sm font-semibold text-[#333]">
        Post a reply
      </div>
      <div className="p-3">
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Write your reply…"
          className="min-h-28"
        />
        <div className="mt-3 flex justify-end">
          <Button
            className="bg-[#105B86] text-white hover:bg-[#0d4f74]"
            onClick={submit}
            disabled={busy}
          >
            {busy ? 'Posting…' : 'Submit'}
          </Button>
        </div>
      </div>
    </div>
  );
}
